import React from 'react';
import { Link } from 'react-router-dom';
import './LarryLineInfoPage.css';

const LarryLineInfoPage: React.FC = () => {
  return (
    <div className="larry-line-info-page">
      <div className="hero-section larry-line">
        <div className="container">
          <div className="hero-content">
            <h1 className="hero-title">The Larry Line</h1>
            <p className="hero-description">Anti-tanking draft tracker for NHL and NBA. See what the draft would look like if losing was not rewarded.</p>
            <div className="cta-buttons">
              <Link to="/larry-line/nhl" className="btn btn-primary btn-large">View NHL Standings</Link>
              <Link to="/larry-line/nba" className="btn btn-secondary btn-large">View NBA Standings</Link>
            </div>
          </div>
        </div>
      </div>

      <section className="features-section">
        <div className="container">
          <h2>The Problem With Tanking</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">📉</div>
              <h3>Losing Is Rewarded</h3>
              <p>The worst teams get the best draft picks. This creates a direct incentive for teams to lose games on purpose.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎟️</div>
              <h3>Fans Pay Full Price</h3>
              <p>Fans buy tickets and watch their team try to lose. Players get benched so the team can fall further in the standings.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🎰</div>
              <h3>The Lottery Did Not Fix It</h3>
              <p>The draft lottery was supposed to discourage tanking, but worse records still mean better lottery odds. The incentive to lose remains.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="workflow-section">
        <div className="container">
          <h2>How The Larry Line Works</h2>
          <div className="workflow-steps">
            <div className="workflow-step">
              <div className="step-number">1</div>
              <h3>Eliminated from Playoffs</h3>
              <p>Once a team is mathematically eliminated from playoff contention, they enter the Larry Pool.</p>
            </div>
            <div className="workflow-step">
              <div className="step-number">2</div>
              <h3>Every Point Counts</h3>
              <p>Post-elimination wins and points determine your draft position. Bad teams get eliminated earlier, giving them more games to earn points.</p>
            </div>
            <div className="workflow-step">
              <div className="step-number">3</div>
              <h3>First Place = First Pick</h3>
              <p>The team with the best record in the Larry Pool after elimination gets the #1 overall draft pick.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="features-section">
        <div className="container">
          <h2>What You Get</h2>
          <div className="features-grid">
            <div className="feature-card">
              <div className="feature-icon">🏒</div>
              <h3>NHL Tracker</h3>
              <p>Live standings showing current Larry Pool rankings, elimination dates, and post-elimination records for all NHL teams.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">🏀</div>
              <h3>NBA Tracker</h3>
              <p>Same anti-tanking analysis for the NBA, tracking which teams would benefit and which would lose out.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📊</div>
              <h3>Historical Comparisons</h3>
              <p>See 5 years of draft comparisons showing how the Larry Line would have changed every draft class.</p>
            </div>
            <div className="feature-card">
              <div className="feature-icon">📅</div>
              <h3>Elimination Timeline</h3>
              <p>Track when each team gets eliminated and enters the Larry Pool throughout the season.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="faq-section">
        <div className="container">
          <h2>Frequently Asked Questions</h2>
          <div className="faq-grid">
            <div className="faq-item">
              <h3>Why is it called The Larry Line?</h3>
              <p>The Larry Line is named after a new baby named Larry. A fresh start, a clean slate, a new way of thinking about the draft. No baggage, no history, just a better system.</p>
            </div>
            <div className="faq-item">
              <h3>Does this actually prevent tanking?</h3>
              <p>Yes. Under The Larry Line, you either go for the playoffs or you go for the #1 pick. There is zero incentive to lose games on purpose.</p>
            </div>
            <div className="faq-item">
              <h3>Is this fair to bad teams?</h3>
              <p>Bad teams get eliminated earlier, which means they have more games to earn points in the Larry Pool. A team eliminated in January has 40+ games. A team eliminated in March has maybe 15.</p>
            </div>
            <div className="faq-item">
              <h3>Is this an official league proposal?</h3>
              <p>No. The Larry Line is a fan-created concept that tracks what would happen if this system was real, using actual NHL and NBA standings data.</p>
            </div>
          </div>
        </div>
      </section>

      <section className="cta-section">
        <div className="container">
          <h2>See The Larry Line In Action</h2>
          <p>Check the current standings and see how the draft would look under this system.</p>
          <div className="cta-buttons">
            <Link to="/larry-line/nhl" className="btn btn-primary btn-large">NHL Larry Line</Link>
            <Link to="/larry-line/nba" className="btn btn-secondary btn-large">NBA Larry Line</Link>
          </div>
        </div>
      </section>
    </div>
  );
};

export default LarryLineInfoPage;
